import { createStore } from 'vuex'
import request from '@/api/request'
import { adminAuthApi, userManageApi, roleManageApi } from '@/api/admin'

function normalizeToken(token) {
    if (!token) return ''
    return String(token).replace(/^Bearer\s+/i, '').trim()
}

export default createStore({
    state: {
        // Admin 认证信息
        adminUser: JSON.parse(localStorage.getItem('admin_user') || 'null'),
        adminToken: localStorage.getItem('admin_token') || '',
        adminPermissions: JSON.parse(localStorage.getItem('admin_permissions') || '[]'),
        adminMenus: JSON.parse(localStorage.getItem('admin_menus') || '[]'),
        
        // 业务区域（环境隔离：由域名固定，不再允许用户切换）
        businessRegion: (window.location.hostname.includes('admin-us.lingxidance.com') ? 'usa' : 'cn'),
        
        // 业务数据（保持兼容）
        user: JSON.parse(localStorage.getItem('user') || 'null'),
        token: localStorage.getItem('token') || '',
        users: [],
        roles: []
    },
    mutations: {
        // ========== Admin 相关 ==========
        SET_ADMIN_USER(state, user) {
            state.adminUser = user
            localStorage.setItem('admin_user', JSON.stringify(user))
        },
        SET_ADMIN_TOKEN(state, token) {
            const normalized = normalizeToken(token)
            state.adminToken = normalized
            localStorage.setItem('admin_token', normalized)
        },
        SET_ADMIN_PERMISSIONS(state, permissions) {
            state.adminPermissions = permissions
            localStorage.setItem('admin_permissions', JSON.stringify(permissions))
        },
        SET_ADMIN_MENUS(state, menus) {
            state.adminMenus = menus
            localStorage.setItem('admin_menus', JSON.stringify(menus))
        },
        CLEAR_ADMIN_AUTH(state) {
            state.adminUser = null
            state.adminToken = ''
            state.adminPermissions = []
            state.adminMenus = []
            localStorage.removeItem('admin_token')
            localStorage.removeItem('admin_refresh_token')
            localStorage.removeItem('admin_user')
            localStorage.removeItem('admin_permissions')
            localStorage.removeItem('admin_menus')
        },
        SET_BUSINESS_REGION(state) {
            // 环境隔离：业务区域由域名决定，忽略外部写入
            state.businessRegion = (window.location.hostname.includes('admin-us.lingxidance.com') ? 'usa' : 'cn')
            localStorage.setItem('business_region', state.businessRegion)
        },
        
        // ========== 业务数据相关（保持兼容） ==========
        SET_USER(state, user) {
            state.user = user
            localStorage.setItem('user', JSON.stringify(user))
        },
        SET_TOKEN(state, token) {
            state.token = token
            localStorage.setItem('token', token)
        },
        CLEAR_AUTH(state) {
            state.user = null
            state.token = ''
            localStorage.removeItem('token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('user')
        },
        SET_USERS(state, users) {
            state.users = users
        },
        SET_ROLES(state, roles) {
            state.roles = roles
        }
    },
    actions: {
        // ========== Admin 登录（新的 Admin Service） ==========
        async adminLogin({ commit }, credentials) {
            try {
                const response = await adminAuthApi.login(credentials)
                console.log('登录响应:', response)
                
                const { code, data, message } = response
                
                if (code === 200 && data && data.token) {
                    commit('SET_ADMIN_TOKEN', data.token)
                    commit('SET_ADMIN_USER', data.admin)
                    commit('SET_ADMIN_PERMISSIONS', data.permissions || [])
                    commit('SET_ADMIN_MENUS', data.menus || [])
                    
                    // 保存 refresh_token
                    if (data.refresh_token) {
                        localStorage.setItem('admin_refresh_token', data.refresh_token)
                    }
                    
                    console.log('登录成功，用户信息已保存')
                    return data
                } else {
                    console.error('登录失败:', { code, data, message })
                    // 抛出错误，包含完整的错误信息，让 Login.vue 处理显示
                    const error = new Error(message || '登录失败')
                    error.code = code
                    error.data = data
                    throw error
                }
            } catch (error) {
                console.error('登录异常:', error)
                throw error
            }
        },
        
        // Admin 登出
        async adminLogout({ commit }) {
            try {
                await adminAuthApi.logout()
            } catch (error) {
                console.warn('Logout API failed:', error)
            }
            commit('CLEAR_ADMIN_AUTH')
        },
        
        // 获取 Admin 用户信息
        async fetchAdminProfile({ commit }) {
            try {
                const response = await adminAuthApi.getProfile()
                const { code, data } = response
                
                if (code === 200) {
                    commit('SET_ADMIN_USER', data.admin)
                    commit('SET_ADMIN_PERMISSIONS', data.permissions || [])
                    commit('SET_ADMIN_MENUS', data.menus || [])
                    return data
                }
            } catch (error) {
                console.warn('获取用户信息失败')
                throw error
            }
        },
        
        // ========== 兼容旧的业务登录（保持兼容，逐步迁移） ==========
        async login({ dispatch }, credentials) {
            // 使用新的 Admin 登录
            return dispatch('adminLogin', credentials)
        },
        
        async register({ commit }, userData) {
            const response = await request.post('/admin/auth/register', userData)
            const { code, data, message } = response
            if (code === 200 && data.token) {
                commit('SET_TOKEN', data.token)
                commit('SET_USER', data)
                localStorage.setItem('refresh_token', data.refresh_token)
                return data
            } else {
                throw new Error(message || '注册失败')
            }
        },
        
        logout({ dispatch }) {
            // 使用新的 Admin 登出
            return dispatch('adminLogout')
        },
        
        // ========== 业务数据获取（使用新的 Admin API） ==========
        async fetchUsers({ commit }, params = {}) {
            try {
                const response = await userManageApi.getList(params)
                const { code, data, message } = response
                if (code === 200) {
                    commit('SET_USERS', data.list || [])
                    return data
                } else {
                    throw new Error(message || '获取用户列表失败')
                }
            } catch (error) {
                console.warn('获取用户列表失败')
                throw error
            }
        },
        
        async fetchRoles({ commit }, params = {}) {
            try {
                const response = await roleManageApi.getList(params)
                const { code, data, message } = response
                if (code === 200) {
                    commit('SET_ROLES', data.list || [])
                    return data
                } else {
                    throw new Error(message || '获取角色列表失败')
                }
            } catch (error) {
                console.warn('获取角色列表失败')
                throw error
            }
        }
    },
    getters: {
        // Admin 认证状态
        isAdminAuthenticated: state => !!state.adminToken,
        currentAdminUser: state => state.adminUser,
        adminPermissions: state => state.adminPermissions,
        adminMenus: state => state.adminMenus,
        
        // 权限检查
        hasPermission: state => (permissionCode) => {
            const isUsHost =
                typeof window !== 'undefined' && window.location.hostname.includes('admin-us.lingxidance.com')
            // 海外站仅做业务操作；系统管理（RBAC/管理员/审计）统一在国内后台，避免跨节点访问 admin 能力
            if (isUsHost && permissionCode && String(permissionCode).startsWith('system:')) {
                return false
            }

            // 超级管理员拥有所有权限
            if (state.adminUser?.role_code === 'super_admin') return true

            // 常规权限命中
            if (state.adminPermissions.includes(permissionCode)) return true

            // 海外站点 JWT-only 兜底：
            // 某些跨域 token 注入场景下，admin_user/permissions 可能尚未落地到本地缓存，
            // 为避免“菜单可点但路由被拦截”的假死体验，暂时放行受保护路由（不含 system:*，见上）。
            if (isUsHost && !!state.adminToken && state.adminPermissions.length === 0) {
                return true
            }

            return false
        },
        
        // 兼容旧的 getter
        isAuthenticated: state => !!state.adminToken || !!state.token,
        currentUser: state => state.adminUser || state.user,
        allUsers: state => state.users,
        allRoles: state => state.roles,
        
        // 业务区域
        currentBusinessRegion: state => state.businessRegion
    }
})
